import { state } from "@/state";
import {
  Box,
  Button,
  Container,
  Grid,
  Heading,
  SlideFade,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { useSnapshot } from "valtio";
import { ClientFeatureBox } from "./ClientFeatureBox";
import { MonthYearToggle } from "../MonthYearToggle";
import { useState, useEffect } from "react";
import { PricingBox } from "./PricingBox";
import { Link } from "@chakra-ui/next-js";
import { DashDivider } from "../LandingPage/DashDivider";
import { Plan } from "@/server/services/plan.service";

const variants = ["canary", "blue", "black"];

export const Prices = ({ plans }: { plans: Plan[] }) => {
  const [mounted, setMounted] = useState(false);
  const snapshot = useSnapshot(state);
  // Use default role during SSR to prevent hydration mismatch
  const role = mounted ? snapshot.role : "coach";
  const t = useTranslations("Pricing");
  const regular = useTranslations();
  const roles = useTranslations("roles");
  const [period, setPeriod] = useState<"month" | "year">("month");

  // Prevent hydration mismatch by only using client-side state after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <Box
      w="full"
      px={{
        base: 4,
        md: 8,
      }}
      py={{
        base: 8,
        md: 24,
      }}
      bgColor="brand.100"
      color="brand.1000"
    >
      <Container maxW="container.xl" id="plans">
        <Stack
          mb={8}
          direction={["column", "row"]}
          justifyContent={"flex-end"}
          opacity={role === "client" ? 0 : 1}
          transition="opacity 0.3s"
          gap={{
            base: 4,
            md: 24,
          }}
        >
          <Text fontWeight="bold">{t("oneMonthFree")}</Text>
          <Box
            textAlign={{
              base: "center",
              md: "right",
            }}
          >
            <MonthYearToggle
              value={period}
              onChange={setPeriod}
              justifyContent={"flex-end"}
            />
          </Box>
        </Stack>
        <Grid
          gap={8}
          gridTemplateColumns={{
            base: "1fr",
            md: "1fr 1fr 1fr",
          }}
        >
          {plans.map((plan, index) => (
            <PricingBox
              key={plan.id}
              oldPrice={
                period === "month"
                  ? plan.oldMonthPrice.toFixed(2)
                  : plan.oldYearPrice.toFixed(2)
              }
              price={
                period === "month"
                  ? plan.monthPrice.toFixed(2)
                  : plan.yearPrice.toFixed(2)
              }
              name={plan.name}
              description={plan.description}
              features={plan.features}
              platformId={plan.platformId.toString()}
              variant={
                variants[index % variants.length] as "canary" | "blue" | "black"
              }
            />
          ))}
        </Grid>
      </Container>
      <Container maxW="container.xl" id="allFeatures">
        {role === "client" && (
          <SlideFade in={mounted && role === "client"} offsetY="20px">
            <Box id="client" gap={8} w="full">
              <Stack
                direction={["column"]}
                alignItems={"center"}
                w="full"
                flex={1}
                gap={4}
              >
                <Stack direction={["column", "row"]} alignItems={"center"}>
                  <Heading
                    flex={1}
                    fontWeight={600}
                    textAlign={{
                      base: "center",
                      md: "left",
                    }}
                    color="brand.1000"
                    fontSize={"38px"}
                  >
                    {regular("plansFor")} {roles("client")}
                  </Heading>
                </Stack>
                <Text flex={1} maxW="520px" textAlign="center">
                  {t("clientFullDescription")}
                </Text>
              </Stack>
              <Grid
                gridTemplateColumns={{
                  base: "1fr",
                  md: "1fr 1fr 1fr",
                }}
                flex={1}
                gap={4}
                w="full"
                mt={16}
              >
                <ClientFeatureBox
                  name={t("clientFreeDiscoveryCallTitle")}
                  description={t("clientFreeDiscoveryCallDescription")}
                />
                <ClientFeatureBox
                  name={t("clientCommitTitle")}
                  description={t("clientCommitDescription")}
                />
                <ClientFeatureBox
                  name={t("clientGrowTitle")}
                  description={t("clientGrowDescription")}
                />
              </Grid>
            </Box>
          </SlideFade>
        )}

        <VStack gap={12} mt={8}>
          <Box>
            <Text fontSize="lg" textAlign="center">
              {regular("needACoachBusinessPlan")}{" "}
              <Link href="/enterprise" textAlign="right" w="full" mt={4}>
                {regular("getInTouch")}
              </Link>
            </Text>
            <Text fontSize="lg" textAlign="center">
              {regular("needAnEnterprisePlan")}{" "}
              <Link href="/enterprise" textAlign="right" w="full" mt={4}>
                {regular("getInTouch")}
              </Link>
            </Text>
          </Box>
          <DashDivider />
          <Link href="#features">
            <Button
              variant="primary"
              size="lg"
              fontSize="2xl"
              fontFamily="Marlide"
            >
              {regular("seeAllFeatures")}
            </Button>
          </Link>
        </VStack>
      </Container>
    </Box>
  );
};
